<template>
<div>
    <v-simple-table>
        <thead>
            <tr>
                <th width="40"></th>
                <th>Label</th>
                <th width="200">Type</th>
                <th width="250">Overschrijven per onderliggend item?
                    <v-tooltip top>
                        <template v-slot:activator="{attrs, on}">
                            <v-icon right v-bind="attrs" v-on="on" small>mdi-information-outline</v-icon>
                        </template>
                        Dit veld kan per onderliggend item worden overschreven. De waarde bij het item zelf dient als standaardwaarde.
                    </v-tooltip>
                </th>
                <th width="300">Gekoppeld
                    <v-tooltip top>
                        <template v-slot:activator="{attrs, on}">
                            <v-icon right v-bind="attrs" v-on="on" small>mdi-information-outline</v-icon>
                        </template>
                        Dit veld enkel weergeven bij specifieke items
                    </v-tooltip>
                </th>
                <th width="60">
                </th>
            </tr>
        </thead>
            <draggable tag="tbody" v-model="fields" group="fields" handle=".handle" animation="200" @change="autosave">
            <tr v-for="(field, fieldIndex) in fields" :key="field.id">
                <td>
                    <v-icon class="handle">mdi-drag</v-icon>
                </td>
                <td>
                    {{ field.name }}
                </td>
                <td>
                    {{ getFieldType(field.type).text }}
                </td>
                <td>
                    {{field.isChildField?'Ja':'Nee'}}
                </td>
                <td>
                    <v-menu open-on-hover offset-y>
                        <template v-slot:activator="{on, attrs}">
                            <div class="single-line" style="max-width:300px;" v-on="on" v-bind="attrs">
                                <v-chip outlined class="mr-1" small v-for="itemId in field.items" :key="itemId">
                                    {{ $helper.getArrayItemByProperty(items, 'id', itemId)?.name }}
                                </v-chip>
                            </div>
                        </template>
                        <eod-card>
                            <v-list dense>
                                <v-list-item v-for="itemId in field.items" :key="'list_'+itemId">
                                    <v-list-item-title>
                                        {{ $helper.getArrayItemByProperty(items, 'id', itemId)?.name }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </eod-card>
                    </v-menu>
                    
                </td>
                <td>
                    <v-tooltip top>
                        <template v-slot:activator="{attrs, on}">
                            <v-btn @click="duplicateField(fieldIndex)" icon v-bind="attrs" v-on="on" small><v-icon small>mdi-content-duplicate</v-icon></v-btn>
                        </template>
                        Aanpassen
                    </v-tooltip>
                    <v-tooltip top>
                        <template v-slot:activator="{attrs, on}">
                            <v-btn @click="editField(fieldIndex)" icon v-bind="attrs" v-on="on" small><v-icon small>mdi-pencil-outline</v-icon></v-btn>
                        </template>
                        Aanpassen
                    </v-tooltip>
                    <v-tooltip top>
                        <template v-slot:activator="{attrs, on}">
                            <v-btn @click="deleteField(fieldIndex)" icon color="error" v-bind="attrs" v-on="on" small><v-icon small>mdi-trash-can-outline</v-icon></v-btn>
                        </template>
                        Verwijderen
                    </v-tooltip>
                </td>
            </tr>
        </draggable>
    </v-simple-table>
    <eod-dialog v-model="isEditDialogVisible" title="Veld" icon="mdi-pencil" iconColor="primary" v-if="activeField" @okAction="saveField" width="800">
        <v-alert class="mt-4" v-if="dialogErrors.length > 0" dense type="error">
            <ul>
                <li v-for="error in dialogErrors" :key="error">{{ error }}</li>
            </ul>
        </v-alert>
        <eod-text-field class="mt-4" label="Label" :rules="[val => $validator.required(val, 'Label')]" required v-model="activeField.name"></eod-text-field>
        <eod-autocomplete class="mt-4" :items="fieldTypes" label="Type" :rules="[val => $validator.required(val, 'Type')]" required v-model="activeField.type" @change="fieldTypeChanged"></eod-autocomplete>
        <template v-if="['list','labels', 'distribution'].includes(activeField.type)">
            <v-alert v-if="listLinkCount > 0" class="mt-4" type="info" text dense>
                <div class="d-flex align-center flex-wrap" style="gap:8px">
                    <div>Deze lijst is ook gekoppeld aan {{ listLinkCount }} ander(e) aangepast(e) veld(en). Wijzigingen aan de lijst en de opties worden ook daar toegepast.</div>
                    <v-spacer></v-spacer>
                    <v-btn small depressed rounded color="info" @click="duplicateList">
                        <v-icon left small>mdi-content-copy</v-icon>Lijst dupliceren
                    </v-btn>
                </div>
            </v-alert>
            <div class="d-flex align-center mt-4">
                <eod-object-autocomplete label="Bestaande lijst" :value="activeList" @input="selectList" endpoint="lists" return-object :fields="['id', 'name', 'itemType', 'listItems{id name threshold color isDeleted}']"></eod-object-autocomplete>
                <v-btn class="ml-2" small depressed color="primary" rounded @click="createNewList"><v-icon left small>mdi-plus</v-icon>Nieuwe lijst</v-btn>
            </div>
            <div v-if="activeList" class="mt-4">
                <eod-text-field class="mb-2" label="Naam van de lijst" v-model="activeList.name"></eod-text-field>
                <v-card outlined>
                    <v-list dense>
                        <v-list-item v-for="item in listItems" :key="'listItem_' + (item.id || item.threshold + '_' + item.name)">
                            <v-list-item-content class="py-1">
                                <v-row dense align="center">
                                    <v-col cols="12" sm="3">
                                        <eod-text-field type="number" step="0.01" label="Waarde" hide-details v-model.number="item.threshold"></eod-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <eod-text-field label="Naam" hide-details v-model="item.name"></eod-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <eod-color-picker label="Kleur" hide-details v-model="item.color" dot-size="15" hide-mode-switch mode="hex"></eod-color-picker>
                                    </v-col>
                                </v-row>
                            </v-list-item-content>
                            <v-btn @click="deleteListItem(item)" class="mt-1" icon color="error"><v-icon>mdi-trash-can-outline</v-icon></v-btn>
                        </v-list-item>
                    </v-list>
                </v-card>
                <v-btn @click="addListItem" color="primary" dense rounded small class="mt-4"><v-icon small>mdi-plus</v-icon>Optie toevoegen</v-btn>
            </div>
        </template>
        <v-switch v-model="activeField.isChildField" label="Overschrijven per onderliggend item"></v-switch>
        <v-autocomplete class="mt-4" clearable chips small-chips :items="items" item-value="id" item-text="name" label="Gekoppeld aan" v-model="activeField.items" multiple outlined></v-autocomplete>
    </eod-dialog>
    <eod-delete-dialog v-if="activeFieldIndex != null" v-model="isDeleteDialogVisible" :item="fields[activeFieldIndex]" @delete="deleteFieldAction"></eod-delete-dialog>
    <div class="d-flex py-2">
        <v-btn @click="addField" class="ml-auto" depressed rounded small color="primary"><v-icon left>mdi-plus</v-icon>Toevoegen</v-btn>
    </div>
</div>
</template>
<script>
import eodObjectAutocomplete from './eod-object-autocomplete.vue'
import eodDeleteDialog from './eod-delete-dialog.vue';
import eodColorPicker from './eod-color-picker.vue';
import draggable from 'vuedraggable';

export default {
    name: 'eod-custom-fields',
    components:{
        eodObjectAutocomplete,
        eodDeleteDialog,
        eodColorPicker,
        draggable
    },
    props:{
        endpoint: {
            type: String,
            required: true,
        }
    },
    data: () => ({
        items:[],
        fields:[],
        savingTimeout: null,
        isDeleteDialogVisible:false,
        isEditDialogVisible:false,
        dialogErrors: [],
        activeFieldIndex:null,
        activeField: null,
        deletedFields: [],
        activeList: null,
        listLinkCount: 0,
        fieldTypes: [
            {
                value: 'text',
                text: 'Tekst'
            },
            {
                value: 'number',
                text: 'Numeriek'
            },
            {
                value: 'password',
                text: 'Wachtwoord'
            },
            {
                value: 'list',
                text: 'Keuzelijst'
            },
            {
                value: 'labels',
                text: 'Labels'
            },
            {
                value: 'distribution',
                text: 'Verdeling'
            }
        ]
    }),
    created(){
        this.$eod.get(this.endpoint, ['id','name']).then(response => {
            if(response.data.data && response.data.data[this.endpoint]){
                this.items = response.data.data[this.endpoint].edges;
            }
        });

        this.loadFields();
    },
    methods:{
        autosave(){
            // Debounced autosave van de volledige veldenset. Wordt aangeroepen na
            // elke voltooide wijziging (veld toevoegen/bewerken/dupliceren/verwijderen
            // /herordenen) i.p.v. via een deep-watcher, om een save→loadFields-lus te
            // vermijden en omdat een index-assign in Vue 2 niet reactief is.
            clearTimeout(this.savingTimeout);
            this.savingTimeout = setTimeout(() => { this.save(); }, 500);
        },
        async saveField(){
            this.dialogErrors = [];

            const field = this.activeField;
            if(!field.name){
                this.dialogErrors.push('Label is verplicht!');
            }

            if(!field.type){
                this.dialogErrors.push('Type is verplicht!');
            }

            const isListType = ['list','labels','distribution'].includes(field.type);
            if(isListType && !this.activeList){
                this.dialogErrors.push('Lijst selecteren is verplicht!');
            }

            if(this.dialogErrors.length > 0){
                return;
            }

            if(isListType){
                // De CustomField-mutatie kent, in tegenstelling tot MeasurementType, geen
                // geneste valueConfigList: de lijst dient hier zelf eerst bewaard te worden
                // zodat we een listId hebben voor field.settings.
                const listData = {...this.activeList};
                listData.listItems = (listData.listItems || []).filter(item => item.isDeleted || (item.name && String(item.name).trim() !== ''));

                const returnfields = ['id', 'name', 'itemType', 'listItems{id name threshold color isDeleted}'];
                let savedList;
                try {
                    const result = await this.$eod.save('List', listData, {
                        update: { returnfields },
                        create: { returnfields }
                    });
                    savedList = (result.data.data.createList) || (result.data.data.updateList);
                } catch {
                    this.dialogErrors.push('Opslaan van de lijst is mislukt.');
                    return;
                }

                this.activeList = savedList;
                field.settings = {...(field.settings || {}), listId: savedList.id};
            } else {
                field.settings = null;
            }

            if(this.activeFieldIndex != null){
                this.fields[this.activeFieldIndex] = {...field};
            } else {
                this.fields.push(field);
            }

            this.isEditDialogVisible = false;
            this.autosave();
        },
        fieldTypeChanged(type){
            if(!['list','labels','distribution'].includes(type)){
                this.activeList = null;
                this.listLinkCount = 0;
            }
        },
        async checkListLinks(listId){
            this.listLinkCount = 0;

            if(!listId){
                return;
            }

            const excludeId = this.activeField && this.activeField.id;

            const response = await this.$eod.get('customFields', ['id', 'settings'], {
                whereIn: {
                    column: 'type',
                    array: ['list', 'labels', 'distribution']
                }
            });

            if(response.data.data && response.data.data.customFields){
                this.listLinkCount = response.data.data.customFields.edges.filter(field =>
                    field.id !== excludeId && field.settings && field.settings.listId === listId
                ).length;
            }
        },
        selectList(list){
            this.activeList = list ? {...list} : null;
            if(this.activeList && !this.activeList.listItems){
                this.activeList.listItems = [];
            }

            this.checkListLinks(list ? list.id : null);
        },
        createNewList(){
            this.activeList = {
                name: this.activeField.name || null,
                itemType: 'VALUE_TO_TEXT',
                listItems: [
                    {
                        threshold: 1,
                        name: null,
                        color: null
                    }
                ]
            };

            this.listLinkCount = 0;
        },
        async loadActiveList(listId){
            const result = await this.$eod.getById('list', listId, ['id', 'name', 'itemType', 'listItems{id name threshold color isDeleted}']);

            if(result.data.data && result.data.data.list){
                this.activeList = result.data.data.list;
                this.checkListLinks(listId);
            }
        },
        duplicateList(){
            if(!this.activeList){
                return;
            }

            // Maak een kopie van de lijst zonder id's zodat bij het bewaren een
            // nieuwe lijst wordt aangemaakt die enkel aan dit veld is gekoppeld.
            const items = this.listItems.map(item => {
                const copy = {...item};
                delete copy.id;
                delete copy.isDeleted;
                return copy;
            });

            this.activeList = {
                name: this.activeList.name ? this.activeList.name + ' kopie' : (this.activeField.name || null),
                itemType: this.activeList.itemType,
                listItems: items
            };

            this.listLinkCount = 0;
        },
        addListItem(){
            if(!this.activeList){
                return;
            }

            if(!this.activeList.listItems){
                this.$set(this.activeList, 'listItems', []);
            }

            let max = 0;
            for (let i = 0; i < this.activeList.listItems.length; i++) {
                const listItem = this.activeList.listItems[i];
                if(listItem.threshold > max){
                    max = listItem.threshold;
                }
            }

            this.activeList.listItems.push({
                threshold: max+1,
                name:'',
                color: null
            });
        },
        deleteListItem(item){
            if(!this.activeList || !this.activeList.listItems){
                return;
            }

            const index = this.activeList.listItems.indexOf(item);
            if(index < 0){
                return;
            }

            if(this.activeList.listItems[index].id){
                this.$set(this.activeList.listItems[index], 'isDeleted', true);
            } else {
                this.activeList.listItems.splice(index, 1);
            }
        },
        getFieldType(type){
            for (let i = 0; i < this.fieldTypes.length; i++) {
                const fieldType = this.fieldTypes[i];
                if(fieldType.value == type){
                    return fieldType;
                }
            }

            return null;
        },
        async loadFields(){
            return this.$eod.get('customFields', ['id','module', 'place', 'type','isChildField','items','name', 'settings'], {
                orderBy: {
                    column: "place",
                    type: "asc"
                },
                where:[{
                    column: 'module',
                    value: this.endpoint,
                }]
            }).then(response => {
                if(response.data.data && response.data.data.customFields){
                    this.fields = response.data.data.customFields.edges;
                }
            })
        },
        async save(){
            let success = true;

            for (let i = 0; i < this.deletedFields.length; i++) {
                const deletedFieldId = this.deletedFields[i];
                await this.$eod.delete('CustomField', deletedFieldId).catch(() => {success = false;});
            }
            for (let i = 0; i < this.fields.length; i++) {
                const field = this.fields[i];
                field.place = i;
                await this.$eod.save('CustomField', field).catch(() => {success = false;});
            }

            await this.loadFields();

            return success;
        },
        duplicateField(fieldIndex){
            const field = {...this.fields[fieldIndex]};
            field.name = field.name + ' kopie';
            delete field.id;
            this.fields.push(field);
            this.autosave();
        },
        addField(){
            this.activeField = {
                name: null,
                module: this.endpoint,
                type: 'text',
                isChildField: false,
                items: null,
                settings: null,
            };

            this.activeFieldIndex = null;
            this.activeList = null;
            this.listLinkCount = 0;
            this.$nextTick(() => {
                this.isEditDialogVisible = true;
            });
        },
        deleteField(fieldIndex){
            this.activeFieldIndex = fieldIndex;

            this.$nextTick(() => {
                this.isDeleteDialogVisible = true;
            });
        },
        editField(fieldIndex){
            this.dialogErrors = [];
            this.activeFieldIndex = fieldIndex;
            this.activeField = {...this.fields[fieldIndex]};
            this.activeList = null;
            this.listLinkCount = 0;

            if(['list','labels','distribution'].includes(this.activeField.type) && this.activeField.settings && this.activeField.settings.listId){
                this.loadActiveList(this.activeField.settings.listId);
            }

            this.$nextTick(() => {
                this.isEditDialogVisible = true;
            });
        },
        deleteFieldAction(){
            if(this.fields[this.activeFieldIndex].id){
                this.deletedFields.push(this.fields[this.activeFieldIndex].id);
            }

            this.fields.splice(this.activeFieldIndex, 1);
            this.isDeleteDialogVisible = false;
            this.activeFieldIndex = null;
            this.autosave();
        }
    },
    computed: {
        listItems(){
            if(!this.activeList || !this.activeList.listItems){
                return [];
            }

            return this.activeList.listItems.filter(item => !item.isDeleted);
        }
    }
}
</script>